import { launchCamera, launchImageLibrary } from 'react-native-image-picker';
import type { MessageKey } from '../i18n/messages';
import { requestAndroidGalleryPermission } from '../screens/ensureAndroidGalleryPermission';
import { requestAndroidCameraPermission } from './requestAndroidCameraPermission';

/**
 * Выбор снимка для аватара — один путь для профиля и для чата.
 *
 * <p>Раньше эта логика была скопирована в `AvatarSettingsSection` и `ChatAvatarEditor` слово в
 * слово, вместе с разрешениями, лимитом на исходник и разбором ответа пикера. Две копии — два
 * места, где чинить каждую находку про Android 14 или про снимок с камеры.
 */

export type AvatarPickSource = 'camera' | 'library';

export type PickedAvatarPhoto = {
  uri: string;
  /** Размеры из пикера; могут быть null — тогда их узнают через `Image.getSize`. */
  width: number | null;
  height: number | null;
  /** Тип из пикера, как есть. Не истина в последней инстанции — см. `animatedAvatarSource`. */
  mimeType: string | null;
  /** Вес исходника: живую (GIF) аватарку мы отправляем как есть, и она может не влезть. */
  sizeBytes: number | null;
};

type AvatarPickResult =
  | { status: 'picked'; photo: PickedAvatarPhoto }
  | { status: 'cancelled' }
  | { status: 'error'; messageKey: MessageKey; detail?: string };

/**
 * Верхняя граница для картинки из пикера. Больше этого мы даже не пытаемся пережимать — это
 * уже не фото для аватара. Сам лимит сервера (8 МБ) проверяется после пережатия, а не по
 * исходнику: раньше снимок с Android-камеры отбраковывался здесь целиком (стори 5.3).
 */
export const MAX_AVATAR_PICK_BYTES = 64 * 1024 * 1024;

export async function pickAvatarPhoto(source: AvatarPickSource): Promise<AvatarPickResult> {
  if (source === 'camera') {
    if (!(await requestAndroidCameraPermission())) {
      return { status: 'error', messageKey: 'avatarCameraDenied' };
    }
  } else if ((await requestAndroidGalleryPermission()) === 'denied') {
    // Частичный доступ («только выбранные фото») — рабочий сценарий, не отказ (п. 6.4).
    return { status: 'error', messageKey: 'galleryPermissionDenied' };
  }

  try {
    // Без `quality`: `processAvatarForUpload` дальше всё равно переупаковывает снимок в JPEG
    // q88, и просить сжатие ещё и у пикера значит жать уже сжатое. Ограничение стороны
    // оставляем — оно бережёт память, а не качество (стори 13.6).
    const res =
      source === 'camera'
        ? await launchCamera({ mediaType: 'photo', saveToPhotos: false })
        : await launchImageLibrary({
            mediaType: 'photo',
            maxWidth: 4096,
            maxHeight: 4096,
            selectionLimit: 1,
          });
    if (res.didCancel) return { status: 'cancelled' };
    if (res.errorCode || res.errorMessage) {
      return {
        status: 'error',
        messageKey: 'settingsAvatarUploadFailed',
        detail: res.errorMessage ?? res.errorCode ?? undefined,
      };
    }
    const a = res.assets?.[0];
    if (!a?.uri) {
      return { status: 'error', messageKey: 'settingsAvatarUploadFailed' };
    }
    if (typeof a.fileSize === 'number' && a.fileSize > MAX_AVATAR_PICK_BYTES) {
      return { status: 'error', messageKey: 'settingsAvatarUploadFailed' };
    }
    const w = typeof a.width === 'number' && a.width > 0 ? Math.round(a.width) : null;
    const h = typeof a.height === 'number' && a.height > 0 ? Math.round(a.height) : null;
    return {
      status: 'picked',
      photo: {
        uri: a.uri,
        width: w,
        height: h,
        mimeType: typeof a.type === 'string' && a.type.length > 0 ? a.type : null,
        sizeBytes: typeof a.fileSize === 'number' && a.fileSize > 0 ? a.fileSize : null,
      },
    };
  } catch (e) {
    return {
      status: 'error',
      messageKey: 'settingsAvatarUploadFailed',
      detail: e instanceof Error ? e.message : undefined,
    };
  }
}
